import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

interface Category {
  _id: string;
  name: string;
}

interface SubCategory {
  _id?: string;
  category: string;
  name: string;
  slug: string;
  description: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
}

const EditSubCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<SubCategory>({
    category: "",
    name: "",
    slug: "",
    description: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
  });
  const [loading, setLoading] = useState(true);

  // ✅ Use base URL from .env
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api/v1";

  // ✅ Fetch categories and subcategory data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, subRes] = await Promise.all([
          axios.get(`${baseUrl}/category`),
          axios.get(`${baseUrl}/subcategory/${id}`)
        ]);

        if (Array.isArray(catRes.data)) {
          setCategories(catRes.data);
        } else if (catRes.data.data) {
          setCategories(catRes.data.data);
        }

        // Handle both possible response formats
        const subData = subRes.data.data || subRes.data;
        setFormData({
          category: subData.category?._id || subData.category || "",
          name: subData.name || "",
          slug: subData.slug || "",
          description: subData.description || "",
          meta_title: subData.meta_title || "",
          meta_description: subData.meta_description || "",
          meta_keywords: subData.meta_keywords || "",
        });
      } catch (err) {
        console.error("❌ Error fetching data:", err);
        alert("Failed to fetch subcategory or category data!");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, baseUrl]);

  // ✅ Handle input changes (auto-generate slug from name)
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "name") {
      const slug = value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-")
        .toLowerCase()
        .replace(/&/g, "-and-")
        .replace(/[^a-z0-9\-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-*/, "")
        .replace(/-*$/, "");

      setFormData((prev) => ({ ...prev, name: value, slug }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ✅ Handle form submission (PUT API)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.put(`${baseUrl}/subcategory/${id}`, formData);
      alert("✅ Sub Category updated successfully!");
      navigate("/settings/subcategory");
    } catch (err) {
      console.error("❌ Error updating subcategory:", err);
      alert("Failed to update subcategory!");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <div className="main-container container-fluid">
      {/* PAGE HEADER */}
      <div className="page-header">
        <h1 className="page-title">Update Sub Category</h1>
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/">Home</a></li>
          <li className="breadcrumb-item active">Sub Category</li>
        </ol>
      </div>

      <div className="row">
        <div className="col-xl-12 col-md-12">
          <form className="card" onSubmit={handleSubmit}>
            <div className="card-body">

              {/* Category Select */}
              <div className="form-group">
                <label className="form-label">Select Category</label>
                <select
                  className="form-select"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sub Category Name */}
              <div className="form-group">
                <label className="form-label">Sub Category Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Slug URL */}
              <div className="form-group">
                <label className="form-label">Slug URL</label>
                <input
                  type="text"
                  className="form-control"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              {/* Meta Title */}
              <div className="form-group">
                <label className="form-label">Meta Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                />
              </div>

              {/* Meta Description */}
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea
                  className="form-control"
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                />
              </div>

              {/* Meta Keywords */}
              <div className="form-group">
                <label className="form-label">Meta Keywords</label>
                <textarea
                  className="form-control"
                  name="meta_keywords"
                  value={formData.meta_keywords}
                  onChange={handleChange}
                />
              </div>

              <button type="submit" className="btn btn-primary">
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSubCategory;
